import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { util as sdkUtil, types as sdkTypes, } from '../util/sdkLoader';
import { denormalisedResponseEntities, ensureOwnListing } from '../util/data';
import * as log from '../util/log';
import { LISTING_STATE_DRAFT } from '../util/types';
import { storableError } from '../util/errors';
import { isUserAuthorized } from '../util/userHelpers';
import {
  ACCEPT_BID_PROCESS_NAME,
  BID_PAYMENT_PROCESS_NAME,
  getStatesNeedingProviderAttention,
  getStatesNeedingCustomerAttention,
  getTransitionsNeedingProviderAttention,
} from '../transactions/transaction';

import { authInfo } from './auth.duck';
import { updateStripeConnectAccount, stripeAccountCreateSuccess } from './stripeConnectAccount.duck';
import { addMarketplaceEntities } from './marketplaceData.duck';
import { crudCategories, crudEventTypes } from '../util/api';

const { UUID } = sdkTypes;

// ================ Helper Functions ================ //

const mergeCurrentUser = (oldCurrentUser, newCurrentUser) => {
  const { id: oId, type: oType, attributes: oAttr, ...oldRelationships } = oldCurrentUser || {};
  const { id, type, attributes, ...relationships } = newCurrentUser || {};

  // Passing null will remove currentUser entity.
  // Only relationships are merged.
  // TODO figure out if sparse fields handling needs a better handling.
  return newCurrentUser === null
    ? null
    : oldCurrentUser === null
      ? newCurrentUser
      : { id, type, attributes, ...oldRelationships, ...relationships };
};

// ================ Async Thunks ================ //

//////////////////////////////////////////////////////////////////////
// Fetch ownListings to check if currentUser has published listings //
//////////////////////////////////////////////////////////////////////

const fetchCurrentUserHasListingsPayloadCreator = (_, thunkAPI) => {
  const { getState, dispatch, extra: sdk, rejectWithValue } = thunkAPI;
  const { currentUser } = getState().user;

  if (!currentUser) {
    return Promise.resolve({ hasListings: false });
  }

  const params = {
    // Since we are only interested in if the user has published
    // listings, we only need at most one result.
    states: 'published',
    page: 1,
    perPage: 1,
  };

  const { businessListingId } = (currentUser && currentUser.id && currentUser.attributes.profile.protectedData) || {};
  const query = businessListingId
    ? sdk.ownListings.show({ id: new UUID(businessListingId) })
    : sdk.ownListings.query(params);

  return query
    .then(response => {
      const hasListings = response.data.data && response.data.data.length > 0;
      const hasListing = response.data.data && response.data.data.id;

      const hasPublishedListings =
        hasListings &&
        ensureOwnListing(response.data.data[0]).attributes.state !== LISTING_STATE_DRAFT;

      if (hasListing) {
        dispatch(addMarketplaceEntities(response));
      }

      return { hasListings: !!hasPublishedListings };
    })
    .catch(e => rejectWithValue(storableError(e)));
};

export const fetchCurrentUserHasListingsThunk = createAsyncThunk(
  'user/fetchCurrentUserHasListings',
  fetchCurrentUserHasListingsPayloadCreator
);

// Backward compatible wrapper for the thunk
export const fetchCurrentUserHasListings = () => (dispatch, getState, sdk) => {
  return dispatch(fetchCurrentUserHasListingsThunk()).unwrap();
};

///////////////////////////////////////////////////////////
// Fetch transactions to check if currentUser has orders //
///////////////////////////////////////////////////////////

const fetchCurrentUserHasOrdersPayloadCreator = (_, { getState, extra: sdk, rejectWithValue }) => {
  if (!getState().user.currentUser) {
    return Promise.resolve({ hasOrders: false });
  }

  const params = {
    only: 'order',
    page: 1,
    perPage: 1,
  };

  return sdk.transactions
    .query(params)
    .then(response => {
      const hasOrders = response.data.data && response.data.data.length > 0;
      return { hasOrders: !!hasOrders };
    })
    .catch(e => rejectWithValue(storableError(e)));
};

export const fetchCurrentUserHasOrdersThunk = createAsyncThunk(
  'user/fetchCurrentUserHasOrders',
  fetchCurrentUserHasOrdersPayloadCreator
);

// Backward compatible wrapper for the thunk
export const fetchCurrentUserHasOrders = () => (dispatch, getState, sdk) => {
  return dispatch(fetchCurrentUserHasOrdersThunk()).unwrap();
};

/////////////////////////////////////////////////////////////////////////////////////
// Fetch transactions in specific states to check if currentUser has notifications //
/////////////////////////////////////////////////////////////////////////////////////

// Notificaiton page size is max (100 items on page)
const NOTIFICATION_PAGE_SIZE = 100;

const fetchCurrentUserNotificationsPayloadCreator = async (_, { extra: sdk, rejectWithValue }) => {
  const statesNeedingProviderAttention = getStatesNeedingProviderAttention() || [];
  const statesNeedingCustomerAttention = getStatesNeedingCustomerAttention() || [];
  const transitionsNeedingAttention = getTransitionsNeedingProviderAttention() || [];

  // const paramsForSales = {
  //   only: 'sale',
  //   states: statesNeedingProviderAttention.map(state => `state/${state}`).join(','),
  //   page: 1,
  //   perPage: NOTIFICATION_PAGE_SIZE,
  // };
  // const paramsForOrders = {
  //   only: 'order',
  //   states: statesNeedingCustomerAttention.map(state => `state/${state}`).join(','),
  //   page: 1,
  //   perPage: NOTIFICATION_PAGE_SIZE,
  // };

  const apiQueryParams = {
    only: 'sale',
    last_transitions: transitionsNeedingAttention,
    page: 1,
    perPage: NOTIFICATION_PAGE_SIZE,
    processNames: ACCEPT_BID_PROCESS_NAME
  };
  const reviewQueryParams = {
    last_transitions: [
      'transition/operator-complete',
      'transition/review-1-by-customer',
      'transition/review-1-by-provider',
    ],
    page: 1,
    perPage: NOTIFICATION_PAGE_SIZE,
    processNames: ACCEPT_BID_PROCESS_NAME,
    include: ['customer', 'provider'],
  };
  const bidQueryParams = {
    only: 'sale',
    last_transitions: transitionsNeedingAttention,
    page: 1,
    perPage: NOTIFICATION_PAGE_SIZE,
    // Bids are created on default-bidpayment, not the legacy default-bid, so
    // this query matched nothing and the new-bid badge never appeared.
    processNames: BID_PAYMENT_PROCESS_NAME
  };

  return Promise.all([
    sdk.transactions.query(bidQueryParams),
    sdk.transactions.query(apiQueryParams),
    sdk.transactions.query(reviewQueryParams),
    // sdk.transactions.query(paramsForSales),
    // sdk.transactions.query(paramsForOrders),
  ])
    .then(([bidTransaction, acceptBidTransaction, reviewBidTransaction]) => {
      const bidNotificationCount = bidTransaction.data.data.length;
      const acceptBidNotificationCount = acceptBidTransaction.data.data.length;
      const reviewNotificationCount = reviewBidTransaction.data.data.length;

      return {
        bidNotificationCount,
        acceptBidNotificationCount,
        reviewNotificationCount,
        bidTransactionData: bidTransaction.data.data,
        acceptBidTransactionData: acceptBidTransaction.data.data,
        reviewBidTransactionData: reviewBidTransaction.data.data,
      };
    })
    .catch(e => rejectWithValue(storableError(e)));
};

export const fetchCurrentUserNotificationsThunk = createAsyncThunk(
  'user/fetchCurrentUserNotifications',
  fetchCurrentUserNotificationsPayloadCreator
);

// Backward compatible wrapper for the thunk
export const fetchCurrentUserNotifications = () => (dispatch, getState, sdk) => {
  return dispatch(fetchCurrentUserNotificationsThunk()).unwrap();
};

const fetchCurrentUserPayloadCreator = (options, thunkAPI) => {
  const { getState, dispatch, extra: sdk, rejectWithValue } = thunkAPI;
  const state = getState();
  const { currentUserHasListings, currentUserShowTimestamp } = state.user || {};
  const { isAuthenticated } = state.auth;
  const {
    callParams = null,
    updateHasListings = true,
    updateNotifications = true,
    afterLogin,
    enforce = false, // Automatic emailVerification might be called too fast
  } = options || {};

  // Double fetch might happen when e.g. profile page is making a full page load
  const aSecondAgo = new Date().getTime() - 1000;
  if (!enforce && currentUserShowTimestamp > aSecondAgo) {
    return Promise.resolve(state.user.currentUser);
  }

  if (!isAuthenticated && !afterLogin) {
    // Make sure current user is null
    return Promise.resolve(null);
  }

  const parameters = callParams || {
    include: ['effectivePermissionSet', 'profileImage', 'stripeAccount'],
    'fields.image': [
      'variants.square-small',
      'variants.square-small2x',
      'variants.square-xsmall',
      'variants.square-xsmall2x',
    ],
    'imageVariant.square-xsmall': sdkUtil.objectQueryString({
      w: 40,
      h: 40,
      fit: 'crop',
    }),
    'imageVariant.square-xsmall2x': sdkUtil.objectQueryString({
      w: 80,
      h: 80,
      fit: 'crop',
    }),
  };

  return sdk.currentUser
    .show(parameters)
    .then(response => {
      const entities = denormalisedResponseEntities(response);
      if (entities.length !== 1) {
        throw new Error('Expected a resource in the sdk.currentUser.show response');
      }
      const currentUser = entities[0];

      // Save stripeAccount to store.stripe.stripeAccount if it exists
      if (currentUser.stripeAccount) {
        dispatch(updateStripeConnectAccount(currentUser.stripeAccount));
      }

      // set current user id to the logger
      log.setUserId(currentUser.id.uuid);
      return currentUser;
    })
    .then(currentUser => {
      // If currentUser is not active (e.g. in 'pending-approval' state),
      // then they don't have listings or transactions that we care about.
      if (isUserAuthorized(currentUser)) {
        if (currentUserHasListings === false && updateHasListings !== false) {
          dispatch(fetchCurrentUserHasListings());
        }

        if (updateNotifications !== false) {
          dispatch(fetchCurrentUserNotifications());
        }

        if (!currentUser.attributes.emailVerified) {
          dispatch(fetchCurrentUserHasOrders());
        }
      }

      dispatch(getCategories());

      // Make sure auth info is up to date
      dispatch(authInfo());
      return currentUser;
    })
    .catch(e => {
      // Make sure auth info is up to date
      dispatch(authInfo());
      log.error(e, 'fetch-current-user-failed');
      return rejectWithValue(storableError(e));
    });
};

export const fetchCurrentUserThunk = createAsyncThunk(
  'user/fetchCurrentUser',
  fetchCurrentUserPayloadCreator
);
// Backward compatible wrapper for the thunk
/**
 * Fetch currentUser API entity.
 *
 * @param {Object} options
 * @param {Object} [options.callParams]           Optional parameters for the currentUser.show().
 * @param {boolean} [options.updateHasListings]   Make extra call for fetchCurrentUserHasListings()?
 * @param {boolean} [options.updateNotifications] Make extra call for fetchCurrentUserNotifications()?
 * @param {boolean} [options.afterLogin]          Fetch is no-op for unauthenticated users except after login() call
 * @param {boolean} [options.enforce]             Enforce the call even if the currentUser entity is freshly fetched.
 */
export const fetchCurrentUser = options => (dispatch, getState, sdk) => {
  return dispatch(fetchCurrentUserThunk(options)).unwrap();
};

/////////////////////////////////////////////
// Send verification email to currentUser //
/////////////////////////////////////////////

const sendVerificationEmailPayloadCreator = (_, { extra: sdk, rejectWithValue }) => {
  return sdk.currentUser
    .sendVerificationEmail()
    .then(() => ({}))
    .catch(e => rejectWithValue(storableError(e)));
};
export const sendVerificationEmailThunk = createAsyncThunk(
  'user/sendVerificationEmail',
  sendVerificationEmailPayloadCreator,
  {
    condition: (_, { getState }) => {
      return !getState()?.user?.sendVerificationEmailInProgress;
    },
  }
);

// Backward compatible wrapper for the thunk
export const sendVerificationEmail = () => (dispatch, getState, sdk) => {
  return dispatch(sendVerificationEmailThunk()).unwrap();
};

/////////////////////////////////////////////
// Fetch categories and event types        //
/////////////////////////////////////////////

const fetchCategoriesPayloadCreator = async (_, { rejectWithValue }) => {
  return Promise.all([
    crudCategories({ query: { isActive: '1' } }),
    crudEventTypes({ query: { isActive: '1' } })
  ])
    .then(([categoriesRes, eventTypesRes]) => {
      // Assuming the API returns { data: [...] } structure
      return {
        categories: categoriesRes.data || [],
        eventTypes: eventTypesRes.data || []
      };
    })
    .catch(e => rejectWithValue(storableError(e)));
};

export const fetchCategoriesThunk = createAsyncThunk(
  'user/fetchCategories',
  fetchCategoriesPayloadCreator
);

export const fetchCategories = () => (dispatch) => {
  return dispatch(fetchCategoriesThunk()).unwrap();
};

// ================ Slice ================ //

const userSlice = createSlice({
  name: 'user',
  initialState: {
    currentUserShowTimestamp: 0,
    currentUserHasListings: false,
    currentUserSaleNotificationCount: 0,
    currentUserOrderNotificationCount: 0,
    categories: [],
    eventTypes: [],
    currentUser: null,
    currentUserShowError: null,
    currentUserHasListings: false,
    currentUserHasListingsError: null,
    currentUserNotificationCount: 0,
    bidTransactionNotificationCount: 0,
    reviewTransactionNotificationCount: 0,
    reviewTransactionRefs: [],
    currentUserNotificationCountError: null,
    currentUserHasOrders: null, // This is not fetched unless unverified emails exist
    currentUserHasOrdersError: null,
    sendVerificationEmailInProgress: false,
    sendVerificationEmailError: null,
  },
  reducers: {
    clearCurrentUser: state => {
      state.currentUser = null;
      state.currentUserShowError = null;
      state.currentUserHasListings = false;
      state.currentUserHasListingsError = null;
      state.currentUserSaleNotificationCount = 0;
      state.currentUserOrderNotificationCount = 0;

      state.currentUserNotificationCountError = null;
    },
    setCurrentUser: (state, action) => {
      state.currentUser = mergeCurrentUser(state.currentUser, action.payload);
    },
    setCurrentUserHasOrders: state => {
      state.currentUserHasOrders = true;
    },
    currentUserShowSuccess: (state, action) => {
      state.currentUser = mergeCurrentUser(state.currentUser, action.payload);
    },
    fetchCurrentUserHasOrdersSuccess: (state, action) => {
      state.currentUserHasOrders = action.payload;
    }
  },
  extraReducers: builder => {
    builder
      // fetchCurrentUser
      .addCase(fetchCurrentUserThunk.pending, state => {
        state.currentUserShowError = null;
      })
      .addCase(fetchCurrentUserThunk.fulfilled, (state, action) => {
        state.currentUser = mergeCurrentUser(state.currentUser, action.payload);
        state.currentUserShowTimestamp = action.payload ? new Date().getTime() : 0;
      })
      .addCase(fetchCurrentUserThunk.rejected, (state, action) => {
        // eslint-disable-next-line no-console
        console.error(action.payload);
        state.currentUserShowError = action.payload;
      })
      // fetchCurrentUserHasListings
      .addCase(fetchCurrentUserHasListingsThunk.pending, state => {
        state.currentUserHasListingsError = null;
      })
      .addCase(fetchCurrentUserHasListingsThunk.fulfilled, (state, action) => {
        state.currentUserHasListings = action.payload.hasListings;
      })
      .addCase(fetchCurrentUserHasListingsThunk.rejected, (state, action) => {
        console.error(action.payload); // eslint-disable-line
        state.currentUserHasListingsError = action.payload;
      })
      // fetchCurrentUserNotifications
      .addCase(fetchCurrentUserNotificationsThunk.pending, state => {
        state.currentUserNotificationCountError = null;
      })
      .addCase(fetchCurrentUserNotificationsThunk.fulfilled, (state, action) => {
        // state.currentUserSaleNotificationCount = action.payload.saleNotificationsCount;
        // state.currentUserOrderNotificationCount = action.payload.orderNotificationsCount;
        state.currentUserNotificationCount = action.payload.acceptBidNotificationCount;
        state.bidTransactionNotificationCount = action.payload.bidNotificationCount;
        state.reviewTransactionNotificationCount = action.payload.reviewNotificationCount;
        // Store transaction refs for review transactions
        if (action.payload.reviewBidTransactionData) {
          state.reviewTransactionRefs = action.payload.reviewBidTransactionData.map(tx => ({
            id: tx.id,
            type: tx.type,
          }));
        }
      })
      .addCase(fetchCurrentUserNotificationsThunk.rejected, (state, action) => {
        console.error(action.payload); // eslint-disable-line
        state.currentUserNotificationCountError = action.payload;
      })
      // fetchCurrentUserHasOrders
      .addCase(fetchCurrentUserHasOrdersThunk.pending, state => {
        state.currentUserHasOrdersError = null;
      })
      .addCase(fetchCurrentUserHasOrdersThunk.fulfilled, (state, action) => {
        state.currentUserHasOrders = action.payload.hasOrders;
      })
      .addCase(fetchCurrentUserHasOrdersThunk.rejected, (state, action) => {
        console.error(action.payload); // eslint-disable-line
        state.currentUserHasOrdersError = action.payload;
      })
      // sendVerificationEmail
      .addCase(sendVerificationEmailThunk.pending, state => {
        state.sendVerificationEmailInProgress = true;
        state.sendVerificationEmailError = null;
      })
      .addCase(sendVerificationEmailThunk.fulfilled, state => {
        state.sendVerificationEmailInProgress = false;
      })
      .addCase(sendVerificationEmailThunk.rejected, (state, action) => {
        state.sendVerificationEmailInProgress = false;
        state.sendVerificationEmailError = action.payload;
      })
      // fetchCategories
      .addCase(fetchCategoriesThunk.fulfilled, (state, action) => {
        state.categories = action.payload.categories;
        state.eventTypes = action.payload.eventTypes;
      });
  },
});

export default userSlice.reducer;

export const { clearCurrentUser, setCurrentUser, setCurrentUserHasOrders, currentUserShowSuccess, fetchCurrentUserHasOrdersSuccess } = userSlice.actions;

// ================ Action Creators for Categories ================ //

const FETCH_CATEGORIES_REQUEST = 'user/FETCH_CATEGORIES_REQUEST';
const FETCH_CATEGORIES_SUCCESS = 'user/FETCH_CATEGORIES_SUCCESS';
const FETCH_CATEGORIES_ERROR = 'user/FETCH_CATEGORIES_ERROR';

const fetchRequest = () => ({ type: FETCH_CATEGORIES_REQUEST });
const fetchSuccess = response => ({
  type: FETCH_CATEGORIES_SUCCESS,
  payload: response,
});
const fetchError = e => ({
  type: FETCH_CATEGORIES_ERROR,
  error: true,
  payload: e,
});

// ================ Selectors ================ //

export const hasCurrentUserErrors = state => {
  const { user } = state;
  return (
    user.currentUserShowError ||
    user.currentUserHasListingsError ||
    user.currentUserNotificationCountError ||
    user.currentUserHasOrdersError
  );
};

export const getCategories = fetchCategories;